/**
 * Neutralisations (M8): safety car / VSC / red-flag periods of a race, from
 * `manifest.time_base.neutralisations` (`[{kind, t0, t1, lap0, lap1, source}]`, the exporter's
 * `track_status_periods`). Yellows are not periods. Monza 2024 has none; the field is read
 * defensively (a local type until manifest.gen.ts ships it), so an older manifest is simply
 * "no neutralisations".
 */
import type { Manifest } from '../bundle/manifest.gen.ts';

export type NeutralKind = 'sc' | 'vsc' | 'red';

export interface NeutralPeriod {
  kind: NeutralKind;
  /** race t the period starts (inclusive) */
  t0: number;
  /** race t it ends (exclusive; the next AllClear) */
  t1: number;
  /** leader lap at t0 / t1, when the exporter derived them. The bundle ALWAYS carries them
   * (schema `$defs/Neutralisation` requires lap0/lap1/source); they stay optional here only for
   * periods this core builds itself (tests, synthetic fixtures) — never assume a manifest gap. */
  lap0?: number;
  lap1?: number;
  /** provenance (e.g. 'track_status'); display-only. Bundle-required, see lap0. */
  source?: string;
  /** reconstructed races: set on a period derived from lap times (e.g. 'SC LAPS FROM LAP TIMES') */
  label?: string;
}

const KINDS: readonly NeutralKind[] = ['sc', 'vsc', 'red'];

/** The race's neutralisation periods, sorted by t0 (defensive: junk entries dropped, never throws). */
export function neutralPeriods(man: Manifest): NeutralPeriod[] {
  const raw = (man.time_base as { neutralisations?: unknown }).neutralisations;
  if (!Array.isArray(raw)) return [];
  const out: NeutralPeriod[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const p = e as Partial<NeutralPeriod>;
    if (!KINDS.includes(p.kind as NeutralKind)) continue;
    if (typeof p.t0 !== 'number' || typeof p.t1 !== 'number' || !Number.isFinite(p.t0) || !Number.isFinite(p.t1) || p.t1 <= p.t0) continue;
    const period: NeutralPeriod = { kind: p.kind as NeutralKind, t0: p.t0, t1: p.t1 };
    if (typeof p.lap0 === 'number' && Number.isFinite(p.lap0)) period.lap0 = p.lap0;
    if (typeof p.lap1 === 'number' && Number.isFinite(p.lap1)) period.lap1 = p.lap1;
    if (typeof p.source === 'string') period.source = p.source;
    if (typeof p.label === 'string') period.label = p.label;
    out.push(period);
  }
  out.sort((a, b) => a.t0 - b.t0);
  return out;
}

/** The period active at race t, or null. Start inclusive, end exclusive (the ending frame is clear). */
export function neutralAt(periods: readonly NeutralPeriod[], t: number): NeutralPeriod | null {
  for (const p of periods) {
    if (t >= p.t0 && t < p.t1) return p;
  }
  return null;
}

/** What styles/shell query on the style context (`ctx.neutral`). */
export interface NeutralApi {
  readonly periods: readonly NeutralPeriod[];
  /** neutralAt over the manifest periods */
  at(t: number): NeutralPeriod | null;
}

export function neutralApiOf(man: Manifest): NeutralApi {
  const periods = neutralPeriods(man);
  return { periods, at: (t: number) => neutralAt(periods, t) };
}
